// ============================================================================
// Module 15.0: Tasks & Actions - Frontend API Service
// ============================================================================

import { Action, Task, TaskChecklist, CentralWorkQueueMetrics } from './types';

const API_BASE = '/api/tasks-actions';
const HEADERS = {
  'Content-Type': 'application/json',
  'x-organization-id': 'ORG-XYZ',
};

export const tasksActionsService = {
  async getWorkQueueMetrics(userId?: string): Promise<CentralWorkQueueMetrics> {
    const url = userId ? `${API_BASE}/analytics/queue-metrics?user_id=${userId}` : `${API_BASE}/analytics/queue-metrics`;
    const res = await fetch(url, { headers: HEADERS });
    const data = await res.json();
    return data.data;
  },

  async getMetrics(userId?: string): Promise<CentralWorkQueueMetrics> {
    return this.getWorkQueueMetrics(userId);
  },

  async getActions(filter?: { status?: string; source_type?: string; assignee?: string }): Promise<Action[]> {
    let url = `${API_BASE}/actions`;
    const params = new URLSearchParams();
    if (filter?.status) params.append('status', filter.status);
    if (filter?.source_type) params.append('source_type', filter.source_type);
    if (filter?.assignee) params.append('assignee', filter.assignee);
    if (params.toString()) url += `?${params.toString()}`;

    const res = await fetch(url, { headers: HEADERS });
    const data = await res.json();
    return data.data || [];
  },

  async createAction(payload: Partial<Action>): Promise<Action> {
    const res = await fetch(`${API_BASE}/actions`, {
      method: 'POST',
      headers: HEADERS,
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    return data.data;
  },

  async updateActionStatus(actionId: string, status: string, actor: string, reason?: string): Promise<Action> {
    const res = await fetch(`${API_BASE}/actions/${actionId}/status`, {
      method: 'POST',
      headers: HEADERS,
      body: JSON.stringify({ status, actor, reason }),
    });
    const data = await res.json();
    return data.data;
  },

  async verifyAction(actionId: string, verifierId: string, result: string, comments: string): Promise<any> {
    const res = await fetch(`${API_BASE}/actions/${actionId}/verify`, {
      method: 'POST',
      headers: HEADERS,
      body: JSON.stringify({ verifier_id: verifierId, result, comments }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error);
    return data.data;
  },

  async getTasks(filter?: { status?: string; action_id?: string; assignee?: string }): Promise<Task[]> {
    let url = `${API_BASE}/tasks`;
    const params = new URLSearchParams();
    if (filter?.status) params.append('status', filter.status);
    if (filter?.action_id) params.append('action_id', filter.action_id);
    if (filter?.assignee) params.append('assignee', filter.assignee);
    if (params.toString()) url += `?${params.toString()}`;

    const res = await fetch(url, { headers: HEADERS });
    const data = await res.json();
    return data.data || [];
  },

  async createTask(payload: Partial<Task>): Promise<Task> {
    const res = await fetch(`${API_BASE}/tasks`, {
      method: 'POST',
      headers: HEADERS,
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    return data.data;
  },

  async completeTask(taskId: string, actor: string, notes?: string): Promise<Task> {
    const res = await fetch(`${API_BASE}/tasks/${taskId}/complete`, {
      method: 'POST',
      headers: HEADERS,
      body: JSON.stringify({ actor, notes }),
    });
    const data = await res.json();
    return data.data;
  },

  async getTaskChecklists(taskId: string): Promise<TaskChecklist[]> {
    const res = await fetch(`${API_BASE}/tasks/${taskId}/checklists`, { headers: HEADERS });
    const data = await res.json();
    return data.data || [];
  },

  async completeChecklistItem(itemId: string, actor: string): Promise<any> {
    const res = await fetch(`${API_BASE}/checklist-items/${itemId}/complete`, {
      method: 'POST',
      headers: HEADERS,
      body: JSON.stringify({ actor }),
    });
    const data = await res.json();
    return data.data;
  },

  async reportBlocker(taskId: string, blockerType: string, description: string): Promise<any> {
    const res = await fetch(`${API_BASE}/tasks/${taskId}/blockers`, {
      method: 'POST',
      headers: HEADERS,
      body: JSON.stringify({ blocker_type: blockerType, description }),
    });
    const data = await res.json();
    return data.data;
  },

  async resolveBlocker(blockerId: string, actor: string): Promise<any> {
    const res = await fetch(`${API_BASE}/blockers/${blockerId}/resolve`, {
      method: 'POST',
      headers: HEADERS,
      body: JSON.stringify({ actor }),
    });
    const data = await res.json();
    return data.data;
  },
};
